"use client"

import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Search, ChevronRight, ChevronUp, ChevronDown, X, UserIcon, Download, Loader2 } from "lucide-react"
import { Calendar } from "../../components/ui/calendar"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Badge } from "../../components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import AddUserModal from "../../components/AddUserModal"
import AddApproachModal from "../../components/AddApproachModal"
import CallbackModal from "../../components/CallbackModal"
import {
  exportApproaches,
  getApproaches,
  getCompanyApproaches,
  updateApproach,
  type ExportApproachesResponse,
} from "../../services/approachesapi"
import { getMySubordinates } from "../../services/usersapi"
import { format } from "date-fns"
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-hot-toast" 
import { cn } from "../../components/lib/utils"
import { useRbac } from "../../context"
import * as XLSX from "xlsx"
import AddCompanyModal from "../../components/AddCompanyApproch"
import ReactSelect from "react-select"
import { getJobsForSelection } from "../../services/jobsapi"
import ApproachWhatsAppChatPopover, {
  type ApproachWebhookEvent,
  type ApproachWhatsAppChat,
  WHATSAPP_OUTCOME_FILTER_OPTIONS,
} from "../../components/approaches/ApproachWhatsAppChatPopover"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { Textarea } from "../../components/ui/textarea"

type ApproachNoteModalMode = "not_interested" | "note"

const ApproachNoteModal: React.FC<{
  isOpen: boolean
  mode: ApproachNoteModalMode
  candidateName?: string
  initialNote?: string
  isSubmitting?: boolean
  onClose: () => void
  onSubmit: (note: string) => void
}> = ({ isOpen, mode, candidateName, initialNote = "", isSubmitting = false, onClose, onSubmit }) => {
  const [note, setNote] = useState(initialNote)
  const [error, setError] = useState("")

  useEffect(() => {
    if (isOpen) {
      setNote(initialNote || "")
      setError("")
    }
  }, [isOpen, initialNote])

  const title = mode === "not_interested" ? "Mark Not Interested" : "Add Note"
  const description =
    mode === "not_interested"
      ? `Add a required note explaining why ${candidateName || "this candidate"} is not interested.`
      : `Add or update a note for ${candidateName || "this candidate"}.`

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent
        className="max-w-md w-[95vw]"
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          document.body.style.removeProperty("pointer-events")
          document.body.removeAttribute("data-scroll-locked")
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Note <span className="text-red-500">*</span>
          </label>
          <Textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              if (error) setError("")
            }}
            placeholder="Enter note..."
            className="min-h-[110px]"
            disabled={isSubmitting}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            className={
              mode === "not_interested"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-orange-600 hover:bg-orange-700 text-white"
            }
            disabled={isSubmitting}
            onClick={() => {
              const trimmed = note.trim()
              if (!trimmed) {
                setError("Note is required")
                return
              }
              onSubmit(trimmed)
            }}
          >
            {isSubmitting ? "Saving..." : mode === "not_interested" ? "Mark Not Interested" : "Save Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const jobPortals = [
  "EarlyJobs",
  "Naukri.com",
  "Indeed India",
  "LinkedIn Jobs",
  "Foundit",
  "Shine.com",
  "TimesJobs",
  "Glassdoor",
  "Google Jobs",
  "Freshersworld",
  "Apna.co",
  "Cutshort",
  "Hirect",
  "Hirist",
  "LetsIntern",
  "JobsForHer",
  "WorkIndia",
  "Wellfound",
  "QuikrJobs",
  "Jobsora",
  "WisdomJobs",
  "Jagran Josh Jobs",
  "CareerIndia",
  "CareerAge",
  "Neuvoo / Talent.com",
  "Recruit.net",
  "WhatJobs.com",
  "JobSquare",
  "SarkariNaukri.com",
  "FreeJobAlert.com",
  "RozgarIndia",
  "JobGranth",
  "NaukriHub",
  "MonsterGulf",
  "CareerJet India",
  "JobSarkari",
  "Times Ascent",
  "Updazz",
  "Instahyre",
  "PlacementIndia",
  "SarkariResult.com",
  "FreshersLive",
  "JobStreet India",
  "PlacementIndia.com",
  "Youth4Work",
  "JobNukkad",
  "TeamLease Jobs",
  "Babajob",
  "Jooble India",
  "ClickIndia Jobs",
  "Olx Jobs",
  "Internshala",
  "Twenty19",
  "KillerLaunch",
  "Placement Season",
  "HelloIntern",
  "Edugorilla Jobs",
  "RecruitNXT",
  "FirstNaukri",
  "JobBuzz",
  "Peeljobs",
  "MyJobStreet",
  "DreamStarts",
  "BigShyft",
  "iimjobs",
  "Engineeristic",
  "PharmaTutor Jobs",
  "MedJobsIndia",
  "BioJobIndia",
  "CXOJobs",
  "JobSahaayata",
  "WorkIndia.in",
  "QuickJobs",
  "Youth4Work Jobs",
  "Rozgar Bazaar",
  "MahaJobs (Maharashtra Govt.)",
  "TNVelaiPortal (Tamil Nadu Govt.)",
  "Kerala PSC Jobs Portal",
  "MP Rojgar Portal",
  "Haryana Rojgar Portal",
  "UP Rojgar Sangam",
  "SkillConnect Portal (Karnataka Govt.)",
  "Punjab Ghar Ghar Rozgar",
  "Himachal Rojgar Portal",
  "Chhattisgarh Rojgar Portal",
  "Odisha Employment Portal",
  "Assam Career",
  "SarkariExam.com",
  "SarkariJobFind.com",
  "EmploymentNews.gov.in",
  "JobMela.in",
  "Jobhai.com",
  "Rocket.in",
  "GigIndia",
  "FlexingIt",
  "Truelancer",
  "WorkNHire",
  "Freelancer.in",
  "Upwork India",
  "Fiverr India",
  "Others"
]


const statusOptions = ["Call Back","Interested","Schedule","Ringing Not Reachable", "Not Reachable", "Not Interested", "Not Relevant", "Salary Issue", "Incorrect Number", "DND", "Switched Off", "Location Issue", "Already Employed" ]
const communicationStatusFilterOptions = ["Schedule", "Not Interested", "Call Back", "In Progression"]
const appStatusOptions = [
  "Applied",
  "Not Scheduled",
  "Interview Rescheduled",
  "Interview Scheduled",
  "Interview Today",
  "Multi-Level Interview",
  "Selected",
  "Offered",
  "Joined",
  "Rejected by Company",
  "Interview Attended",
  "Interview Not Attended",
  "Not Joined",
]

type SortableHeaderProps = {
  column: string
  className?: string
  children: React.ReactNode
}

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#F5F1F9] z-10",
        className
      )}
      // onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {/* {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )} */}
      </div>
    </th>
  );

// Define the User type
type User = {
  id: string
  name: string
  phone: string
  email: string
  ledBy?: string
  approachedBy?: string
  qualifications?: string
  city?: string
  source?: string
  status?: string
  createdAt?: string
  callbackDate?: string
  callbackTime?: string
  scheduleDate?: string
  scheduleTime?: string
  webhookEvents?: ApproachWebhookEvent[]
  whatsappChat?: ApproachWhatsAppChat
  recruiter?: {
    id?: string
    email?: string
    username?: string
    [key: string]: any
  }
  manager?: {
    id?: string
    email?: string
    username?: string
    [key: string]: any
  }
  [key: string]: any
}

type JobFilterOption = {
  value: string
  label: string
}

const UserTable: React.FC<{
  users: User[]
  jobOptions: JobFilterOption[]
  setUsers: React.Dispatch<React.SetStateAction<User[]>>
  onUserSelect: (user: User | null) => void
  expandedRows: string[]
  setExpandedRows: React.Dispatch<React.SetStateAction<string[]>>
  sortBy: string
  sortOrder: "asc" | "desc"
  onSort: (field: string) => void
  onUserReassign: (user: User) => void
  onSourceChange: (userId: string, newSource: string) => void
  onStatusChange: (userId: string, newStatus: string) => void
  onCallbackSubmit: (userId: string, date: string, time: string) => void
  onScheduleSubmit: (userId: string, date: string, time: string) => void
  onRequestNotInterested: (userId: string) => void
  onRequestNote: (userId: string) => void
  setCallbackModalUserId: React.Dispatch<React.SetStateAction<string | null>>
  isLoading: boolean
  onUserEdit?: (user: User) => void
  isCommunicationView?: boolean
}> = ({
  users,
  jobOptions,
  setUsers,
  onUserSelect,
  expandedRows,
  setExpandedRows,
  sortBy,
  sortOrder,
  onSort,
  onUserReassign,
  onSourceChange,
  onStatusChange,
  onCallbackSubmit,
  onScheduleSubmit,
  onRequestNotInterested,
  onRequestNote,
  setCallbackModalUserId,
  isLoading,
  onUserEdit,
  isCommunicationView = false,
}) => {
  const toggleRow = (id: string) => {
    setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]))
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null
    return sortOrder === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
  }

  const [callbackDate, setCallbackDate] = useState<Date | undefined>()
  const [callbackTime, setCallbackTime] = useState("")
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>()
  const [scheduleTime, setScheduleTime] = useState("")
  const [callbackPopoverOpen, setCallbackPopoverOpen] = useState<string | null>(null)
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState<string | null>(null)
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null)
  const [infoPopoverUserId, setInfoPopoverUserId] = useState<string | null>(null)
  const [callbackModalUserIdState, setCallbackModalUserIdState] = useState<string | null>(null)
  const [scheduleConfirmUserId, setScheduleConfirmUserId] = useState<string | null>(null)
  const [showScheduleUIFor, setShowScheduleUIFor] = useState<string | null>(null)

  const getJobDisplay = (jobValue: any) => {
    if (!jobValue) return "N/A"
    if (typeof jobValue === "string") {
      const matchedJob = jobOptions.find((job) => job.value === jobValue)
      return matchedJob?.label || jobValue
    }
    if (typeof jobValue === "object") {
      const title = jobValue?.title || ""
      const company = jobValue?.companyId?.name || ""
      const id = jobValue?._id || jobValue?.id || ""
      if (title && company) return `${title} - ${company}`
      if (title) return title
      if (company) return company
      if (id) return String(id)
    }
    return String(jobValue)
  }

  const handleCallbackSubmitLocal = (userId: string) => {
    if (callbackDate && callbackTime) {
      onCallbackSubmit(userId, format(callbackDate, "yyyy-MM-dd"), callbackTime)
      setCallbackDate(undefined)
      setCallbackTime("")
    }
  }

  const handleScheduleSubmitLocal = (userId: string) => {
    if (scheduleDate && scheduleTime) {
      onScheduleSubmit(userId, format(scheduleDate, "yyyy-MM-dd"), scheduleTime)
      setScheduleDate(undefined)
      setScheduleTime("")
    }
  }

  const formatPersonLabel = (person?: {
    email?: string
    username?: string
    name?: string
  } | null, fallback?: string) =>
    person?.email || person?.username || person?.name || fallback || "N/A"

  const stickyDateCellClass =
    "p-4 min-w-40 sticky left-0 z-10 border-r border-border bg-white group-hover:bg-gray-50"
  const stickyNameCellClass =
    "p-4 min-w-48 sticky left-40 z-10 border-r border-border bg-white group-hover:bg-gray-50"

  const formatCreatedAt = (value?: string) => {
    if (!value) return "N/A"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "N/A"
    return format(date, "dd MMM yyyy, HH:mm")
  }

  return (
    <div className="rounded-lg shadow-sm">
      {/* Desktop Table View */}
      <div className="hidden lg:block min-w-full">
          <table className="min-w-full divide-y divide-border">

        <thead style={{ backgroundColor: '#FFF5F2' }}>
          
            <tr>
              <SortableHeader column="createdAt" className="min-w-40 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                Date & Time
              </SortableHeader>
              <SortableHeader column="username" className="min-w-48 sticky left-40 bg-[#FFF5F2] z-20 border-r border-border">
                Name
              </SortableHeader>
              <SortableHeader column="phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
              {isCommunicationView && (
                <th
                  className="px-3 py-3 min-w-[7.5rem] border-l border-border text-center text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 z-10 bg-[#FFF5F2]"
                  aria-label="AI Response"
                >
                  AI Response
                </th>
              )}
              <SortableHeader column="email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
              <SortableHeader column="approachedBy" className="bg-[#FFF5F2] min-w-48">Approached By</SortableHeader>
              <SortableHeader column="ledBy" className="bg-[#FFF5F2] min-w-48">Led By</SortableHeader>
              <SortableHeader column="role" className="bg-[#FFF5F2] min-w-32">Qualification</SortableHeader>
              <SortableHeader column="city" className="bg-[#FFF5F2] min-w-32">City</SortableHeader>
              {!isCommunicationView && (
                <SortableHeader column="location" className="bg-[#FFF5F2] min-w-48">Source</SortableHeader>
              )}
              <SortableHeader column="status" className="bg-[#FFF5F2] min-w-40">Status</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-40 sticky top-0 bg-[#FFF5F2] z-10">
                Application Status
              </th>
              {isCommunicationView && (
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Note
                </th>
              )}
              {!isCommunicationView && (
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Status Details
                </th>
              )}
            </tr>
          </thead>
        
        <tbody>
          {isLoading ? (
            <>
              {[...Array(10)].map((_, index) => (
                <tr key={`shimmer-${index}`} className="bg-white divide-y divide-border">
                  {/* Date & Time */}
                  <td className={stickyDateCellClass}>
                    <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                  </td>
                  {/* Name */}
                  <td className={stickyNameCellClass}>
                    <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                  </td>
                  {/* Phone */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                  </td>
                  {/* AI Response */}
                  {isCommunicationView && (
                    <td className="p-4 border-l border-border text-center">
                      <div className="flex justify-center">
                        <div className="animate-pulse bg-gray-200 rounded-full w-7 h-7"></div>
                      </div>
                    </td>
                  )}
                  {/* Email */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                  </td>
                  {/* Approached By */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-36 h-4"></div>
                  </td>
                  {/* Led By */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-36 h-4"></div>
                  </td>
                  {/* Qualification */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                  </td>
                  {/* City */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                  </td>
                  {/* Source */}
                  {!isCommunicationView && (
                    <td className="p-4">
                      <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                    </td>
                  )}
                  {/* Status */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-40 h-10"></div>
                  </td>
                  {/* Application Status */}
                  <td className="p-4">
                    <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                  </td>
                  {/* Note */}
                  {isCommunicationView && (
                    <td className="p-4">
                      <div className="animate-pulse bg-gray-200 rounded w-20 h-7"></div>
                    </td>
                  )}
                  {/* Status Details */}
                  {!isCommunicationView && (
                    <td className="p-4">
                      <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                    </td>
                  )}
                </tr>
              ))}
            </>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={12} className="p-8 text-center text-muted-foreground">
                No users found
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <React.Fragment key={user.id}>
                <tr 
                  className="border-b border-gray-200 hover:bg-gray-50 transition-colors group"
                >
                  <td className={`${stickyDateCellClass} text-gray-600 text-sm whitespace-nowrap`}>
                    {formatCreatedAt(user.createdAt)}
                  </td>
                  <td className={`${stickyNameCellClass} text-gray-800`}>
                    <Popover
                      open={infoPopoverUserId === user.id}
                      onOpenChange={(open) => setInfoPopoverUserId(open ? user.id : null)}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-left hover:underline underline-offset-2"
                        >
                          {user.name}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-80 p-3">
                        <div className="space-y-1.5 text-sm">
                          <div><span className="font-medium">Date & Time:</span> {formatCreatedAt(user.createdAt)}</div>
                          <div><span className="font-medium">Name:</span> {user.name || "N/A"}</div>
                          <div><span className="font-medium">Phone:</span> {user.phone || "N/A"}</div>
                          <div><span className="font-medium">Email:</span> {user.email || "N/A"}</div>
                          <div><span className="font-medium">Approached By:</span> {formatPersonLabel(user.recruiter, user.approachedBy)}</div>
                          <div><span className="font-medium">Led By:</span> {formatPersonLabel(user.manager, user.ledBy)}</div>
                          <div><span className="font-medium">Qualification:</span> {user.qualifications || "N/A"}</div>
                          <div><span className="font-medium">City:</span> {user.city || "N/A"}</div>
                          <div><span className="font-medium">Source:</span> {user.source || "N/A"}</div>
                          <div><span className="font-medium">Status:</span> {user.status || "N/A"}</div>
                          <div><span className="font-medium">Application Status:</span> {user.applications?.currentStatus?.status || user.application?.currentStatus?.status || "N/A"}</div>
                          <div><span className="font-medium">Job:</span> {getJobDisplay(user.jobId)}</div>
                          <div><span className="font-medium">Remarks:</span> {user.remarks || "N/A"}</div>
                          <div><span className="font-medium">Callback:</span> {user.callbackDate && user.callbackTime ? `${user.callbackDate} ${user.callbackTime}` : "N/A"}</div>
                          <div><span className="font-medium">Schedule:</span> {user.scheduleDate && user.scheduleTime ? `${user.scheduleDate} ${user.scheduleTime}` : "N/A"}</div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </td>
                  <td className="p-4 text-gray-600">{user.phone}</td>
                  {isCommunicationView && (
                    <td className="p-4 border-l border-border min-w-[7.5rem] bg-white group-hover:bg-gray-50 text-center">
                      <div className="flex justify-center">
                        <ApproachWhatsAppChatPopover
                          webhookEvents={user.webhookEvents}
                          whatsappChat={user.whatsappChat}
                          candidateName={user.name}
                          phone={user.phone}
                          status={user.status}
                        />
                      </div>
                    </td>
                  )}
                  <td className="p-4 text-gray-600">{user.email===""?"N/A":user.email}</td>
                  <td className="p-4 text-gray-600">{formatPersonLabel(user.recruiter, user.approachedBy)}</td>
                  <td className="p-4 text-gray-600">{formatPersonLabel(user.manager, user.ledBy)}</td>
                  <td className="p-4 text-gray-600">{user.qualifications}</td>
                  <td className="p-4 text-gray-600">{user.city || "N/A"}</td>
                  {!isCommunicationView && (
                    <td className="p-4 text-gray-600">{user.source}</td>
                  )}

                  <td className="p-4">
                    <div className="flex flex-col gap-2">
                      {isCommunicationView ? (
                        <div className="flex flex-col gap-1.5 min-w-[9rem]">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 px-2.5 text-xs border-orange-600 text-orange-600 hover:bg-orange-50 justify-between"
                              >
                                <span>
                                  {user.status === "Schedule"
                                    ? "Scheduled"
                                    : user.status === "Not Interested"
                                      ? "Not Interested"
                                      : user.status === "Call Back"
                                        ? "Call Back"
                                        : user.status === "In Progression"
                                          ? "In Progression"
                                          : "Actions"}
                                </span>
                                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44">
                              <DropdownMenuItem
                                disabled={user.status === "Schedule"}
                                onSelect={() => {
                                  // Defer so DropdownMenu fully unmounts before Dialog opens (avoids body pointer-events freeze)
                                  setTimeout(() => onStatusChange(user.id, "Schedule"), 0)
                                }}
                              >
                                Schedule
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={user.status === "Not Interested" || user.status === "Schedule"}
                                onSelect={() => {
                                  setTimeout(() => onRequestNotInterested(user.id), 0)
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                Not Interested
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={user.status === "Schedule"}
                                onSelect={() => {
                                  setTimeout(() => onStatusChange(user.id, "Call Back"), 0)
                                }}
                              >
                                Call Back
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={user.status === "Schedule" || user.status === "In Progression"}
                                onSelect={() => {
                                  setTimeout(() => onStatusChange(user.id, "In Progression"), 0)
                                }}
                              >
                                In Progression
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ) : (
                      <Select value={user.status || ""} onValueChange={(value) => onStatusChange(user.id, value)}>
                        <SelectTrigger className={`w-40 border-gray-300 focus:ring-2 focus:ring-blue-500 text-left ${user.status === "Schedule" ? "pointer-events-none opacity-50 cursor-not-allowed" : ""}`}>
                          <SelectValue placeholder="Select Status" className="text-left" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option} value={option.trim()} className={`text-left whitespace-nowrap`}>
                              {option.trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      )}
                      
                      {user.status === "Schedule" && showScheduleUIFor === user.id && (
                        <div className="flex flex-col gap-2 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 w-72">
                          <label className="text-xs text-muted-foreground font-medium mb-1">Schedule Date & Time</label>
                          <div className="flex flex-col gap-2">
                            <Popover
                              open={openPopoverId === user.id}
                              onOpenChange={(open) => setOpenPopoverId(open ? user.id : null)}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-gray-300 hover:bg-gray-100 transition-colors w-full justify-start bg-transparent"
                                >
                                  {user.scheduleDate || "Select Date"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 z-50">
                                <Calendar
                                  mode="single"
                                  selected={user.scheduleDate ? new Date(user.scheduleDate) : undefined}
                                  onSelect={(date) => {
                                    setScheduleDate(date)
                                    setOpenPopoverId(null)
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <Input
                              type="time"
                              value={user.scheduleTime || ""}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="w-full border-gray-300 focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleScheduleSubmitLocal(user.id)}
                            className="bg-orange-600 hover:bg-orange-700 text-white w-full mt-1"
                          >
                            Submit
                          </Button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-gray-600">
                    {user.applications?.currentStatus?.status || user.application?.currentStatus?.status || "N/A"}
                  </td>
                  {isCommunicationView && (
                    <td className="p-4">
                      <div className="flex flex-col gap-1.5 min-w-[7rem]">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onRequestNote(user.id)}
                          className="h-8 px-2.5 text-xs border-gray-400 text-gray-700 hover:bg-gray-50"
                        >
                          Note
                        </Button>
                        {user.status === "Call Back" && user.callbackDate && user.callbackTime ? (
                          <p className="text-[11px] text-blue-700 font-medium" title="Callback time">
                            Callback:{" "}
                            {format(
                              new Date(`${user.callbackDate}T${user.callbackTime}`),
                              "dd MMM yyyy, HH:mm",
                            )}
                          </p>
                        ) : null}
                        {user.remarks ? (
                          <p className="text-[11px] text-gray-500 line-clamp-3" title={user.remarks}>
                            {user.remarks}
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-400">No note</p>
                        )}
                      </div>
                    </td>
                  )}
                  {/* New Status Details column */}
                 {!isCommunicationView && (
                 <td className="p-4 text-gray-700">
  {user.status === "Call Back" && user.callbackDate && user.callbackTime ? (
    <span>
      <span className="font-semibold">
        {format(new Date(`${user.callbackDate}T${user.callbackTime}`), "dd MMM yyyy, HH:mm")}
      </span>
    </span>
  ) : (
    <span>{user.status}</span>
  )}
</td>
                 )}
                </tr>
                {expandedRows.includes(user.id) && (
                  <tr>
                    <td colSpan={12} className="p-4 bg-gray-50 border-b border-gray-200">
                      <div className="text-gray-600">Additional details for {user.name} can be added here.</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
          </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        {isLoading ? (
          <div className="space-y-4 p-4">
            {[...Array(5)].map((_, index) => (
              <div key={`mobile-shimmer-${index}`} className="bg-white rounded-lg border p-4">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  <div className="flex gap-2">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No users found
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {users.map((user) => (
              <div 
                key={user.id} 
                className="bg-white rounded-lg border p-4 space-y-3"
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">{formatCreatedAt(user.createdAt)}</p>
                    <h3 className="font-semibold text-lg text-gray-900 truncate">{user.name}</h3>
                    <p className="text-sm text-gray-600 truncate">{user.phone}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <ApproachWhatsAppChatPopover
                      webhookEvents={user.webhookEvents}
                      whatsappChat={user.whatsappChat}
                      candidateName={user.name}
                      phone={user.phone}
                      status={user.status}
                    />
                    <div className="w-full sm:w-auto">
                    {isCommunicationView ? (
                    <div className="flex flex-col gap-1.5 w-full sm:w-40">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-2.5 text-xs border-orange-600 text-orange-600 hover:bg-orange-50 justify-between w-full"
                          >
                            <span>
                              {user.status === "Schedule"
                                ? "Scheduled"
                                : user.status === "Not Interested"
                                  ? "Not Interested"
                                  : user.status === "Call Back"
                                    ? "Call Back"
                                    : user.status === "In Progression"
                                      ? "In Progression"
                                      : "Actions"}
                            </span>
                            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem
                            disabled={user.status === "Schedule"}
                            onSelect={() => {
                              setTimeout(() => onStatusChange(user.id, "Schedule"), 0)
                            }}
                          >
                            Schedule
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={user.status === "Not Interested" || user.status === "Schedule"}
                            onSelect={() => {
                              setTimeout(() => onRequestNotInterested(user.id), 0)
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            Not Interested
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={user.status === "Schedule"}
                            onSelect={() => {
                              setTimeout(() => onStatusChange(user.id, "Call Back"), 0)
                            }}
                          >
                            Call Back
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={user.status === "Schedule" || user.status === "In Progression"}
                            onSelect={() => {
                              setTimeout(() => onStatusChange(user.id, "In Progression"), 0)
                            }}
                          >
                            In Progression
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onRequestNote(user.id)}
                        className="h-8 px-2.5 text-xs border-gray-400 text-gray-700 hover:bg-gray-50 w-full"
                      >
                        Note
                      </Button>
                      {user.status === "Call Back" && user.callbackDate && user.callbackTime ? (
                        <p className="text-[11px] text-blue-700 font-medium">
                          Callback:{" "}
                          {format(
                            new Date(`${user.callbackDate}T${user.callbackTime}`),
                            "dd MMM yyyy, HH:mm",
                          )}
                        </p>
                      ) : null}
                      {user.remarks ? (
                        <p className="text-[11px] text-gray-500 line-clamp-2" title={user.remarks}>
                          {user.remarks}
                        </p>
                      ) : null}
                    </div>
                    ) : (
                    <Select value={user.status || ""} onValueChange={(value) => onStatusChange(user.id, value)}>
                      <SelectTrigger className="w-full sm:w-36 border-gray-300 focus:ring-2 focus:ring-blue-500 text-left h-10">
                        <SelectValue placeholder="Status" className="text-left" />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem key={option} value={option.trim()} className="text-left whitespace-nowrap">
                            {option.trim()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Email:</span>
                    <span className="truncate text-gray-800">{user.email || "N/A"}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-28">Approached By:</span>
                    <span className="truncate text-gray-800">{formatPersonLabel(user.recruiter, user.approachedBy)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-28">Led By:</span>
                    <span className="truncate text-gray-800">{formatPersonLabel(user.manager, user.ledBy)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Qualification:</span>
                    <span className="truncate text-gray-800">{user.qualifications || "N/A"}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">City:</span>
                    <span className="truncate text-gray-800">{user.city || "N/A"}</span>
                  </div>
                  {!isCommunicationView && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">Source:</span>
                    <span className="truncate text-gray-800">{user.source || "N/A"}</span>
                  </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                    <span className="text-gray-600 font-medium min-w-0 sm:w-20">App Status:</span>
                    <span className="truncate text-gray-800">{user.applications?.currentStatus?.status || user.application?.currentStatus?.status || "N/A"}</span>
                  </div>
                </div>

                {/* Status Details */}
                {!isCommunicationView && user.status === "Call Back" && user.callbackDate && user.callbackTime && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-800 mb-1">Callback Scheduled</div>
                    <div className="text-sm text-blue-600 break-words">
                      {format(new Date(`${user.callbackDate}T${user.callbackTime}`), "dd MMM yyyy, HH:mm")}
                    </div>
                  </div>
                )}

                {/* Schedule UI */}
                {user.status === "Schedule" && showScheduleUIFor === user.id && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="text-sm font-medium text-gray-700">Schedule Date & Time</div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Date</label>
                        <Popover
                          open={openPopoverId === user.id}
                          onOpenChange={(open) => setOpenPopoverId(open ? user.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-gray-300 hover:bg-gray-100 transition-colors w-full justify-start bg-white h-10"
                            >
                              {user.scheduleDate || "Select Date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-50" align="start">
                            <Calendar
                              mode="single"
                              selected={user.scheduleDate ? new Date(user.scheduleDate) : undefined}
                              onSelect={(date) => {
                                setScheduleDate(date)
                                setOpenPopoverId(null)
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Time</label>
                        <Input
                          type="time"
                          value={user.scheduleTime || ""}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full border-gray-300 focus:ring-2 focus:ring-blue-500 h-10"
                        />
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleScheduleSubmitLocal(user.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white w-full h-10"
                    >
                      Submit Schedule
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

  )
}

const ScheduleConfirmModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}> = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-6 relative">
        <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl" onClick={onClose}>
          ×
        </button>
        <h2 className="text-lg font-semibold mb-4 text-center">Are you ready for schedule?</h2>
        <div className="flex justify-center gap-3 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={onConfirm}>
            Yes
          </Button>
        </div>
      </div>
    </div>
  )
}

const ApproachesView: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [filters, setFilters] = useState({
    source: "",
    status: "",
    appStatus: "",
    recruiterId: "",
    jobId: "",
    fromDate: "",
    toDate: "",
    whatsappOutcome: "",
  })
  const {user}=useRbac()
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [itemsPerPage] = useState(10)
  const [users, setUsers] = useState<User[]>([])
  const [totalApproaches, setTotalApproaches] = useState(0)
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false)
  const [userToReassign, setUserToReassign] = useState<User | null>(null)
  const [userAdded, setUserAdded] = useState(false)
  const [userEdited, setUserEdited] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditApproachModal, setShowEditApproachModal] = useState(false)
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null)
  const [callbackModalUserId, setCallbackModalUserId] = useState<string | null>(null)
  const [noteModal, setNoteModal] = useState<{
    userId: string
    mode: ApproachNoteModalMode
  } | null>(null)
  const [noteModalSubmitting, setNoteModalSubmitting] = useState(false)
  const [scheduleConfirmUserId, setScheduleConfirmUserId] = useState<string | null>(null)
  const [showScheduleUIFor, setShowScheduleUIFor] = useState<string | null>(null)
  const [previousStatusForSchedule, setPreviousStatusForSchedule] = useState<string | null>(null)
const [recruiters, setRecruiters] = useState<{ id: string; username: string }[]>(
  user !== "ADMIN" 
    ? [{ id: user.id, username: user.username }]
    : []
);
  const [jobOptions, setJobOptions] = useState<JobFilterOption[]>([])
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const navigate = useNavigate() // <-- Add this
  const location = useLocation()
  const isCommunicationView = location.pathname === "/ApprochesView2"
  const canDownloadExcel =
    user?.role === "ADMIN" ||
    user?.role === "SHM" ||
    user?.role === "SHM_ADMIN" ||
    user?.role === "HM"

  // Radix Dialog opened from DropdownMenu can leave `pointer-events: none` on <body>
  useEffect(() => {
    if (!noteModal && !callbackModalUserId && !scheduleConfirmUserId && !showAddModal && !showEditApproachModal) {
      const t = setTimeout(() => {
        document.body.style.removeProperty("pointer-events")
        document.body.removeAttribute("data-scroll-locked")
      }, 0)
      return () => clearTimeout(t)
    }
  }, [noteModal, callbackModalUserId, scheduleConfirmUserId, showAddModal, showEditApproachModal])

  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }]
    if (recruiters.length > 0) {
      recruiters.forEach((recruiter) => {
        options.push({
          value: recruiter.id,
          label: recruiter.username,
        })
      })
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true })
    }
    return options
  }, [recruiters, isLoadingRecruiters])

  const sourceOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Sources" }]
    jobPortals.forEach((portal) => {
      options.push({
        value: portal,
        label: portal,
      })
    })
    return options
  }, [])

  const activeFilters = Object.entries(filters).filter(([_, value]) => value !== "")

  // Update getFilterLabel to handle any filter key
  const getFilterLabel = (key: string) => {
    switch (key) {
      case "source":
        return "Source"
      case "status":
        return "Status"
      case "appStatus":
        return "App Status"
      case "recruiterId":
        return "Recruiter"
      case "jobId":
        return "Job"
      case "fromDate":
        return "From Date"
      case "toDate":
        return "To Date"
      case "whatsappOutcome":
        return "WhatsApp Outcome"
      default:
        // Capitalize first letter for unknown keys
        return key.charAt(0).toUpperCase() + key.slice(1)
    }
  }

  const onSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(field)
      setSortOrder("asc")
    }
    setCursor(null)
    setNextCursor(null)
    setHasNextPage(false)
  }

  const removeFilter = (key: string) => {
    setFilters((prev) => ({ ...prev, [key]: "" }))
    setCursor(null)
    setNextCursor(null)
    setHasNextPage(false)
  }

  const clearAllFilters = () => {
    setFilters({
      source: "",
      status: "",
      appStatus: "",
      recruiterId: "",
      jobId: "",
      fromDate: "",
      toDate: "",
      whatsappOutcome: "",
    })
    setSearchTerm("")
    setCursor(null)
    setNextCursor(null)
    setHasNextPage(false)
  }

  const handleSourceChange = async (userId: string, newSource: string) => {
    setUsers((prevUsers) => prevUsers.map((u) => (u.id === userId ? { ...u, source: newSource } : u)))
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({ ...selectedUser, source: newSource })
    }
    try {
      await updateApproach(userId, { source: newSource })
    } catch (error) {
      // Optionally: revert UI change or show error
      console.error("Failed to update source:", error)
    }
  }

  const handleStatusChange = async (userId: string, newStatus: string) => {
    const previousUser = users.find(u => u.id === userId)
    const previousStatus = previousUser?.status || ""
    
    if (newStatus === "Schedule") {
      // Store previous status before changing to Schedule
      setPreviousStatusForSchedule(previousStatus)
      setScheduleConfirmUserId(userId)
      setShowScheduleUIFor(null)
    }
    
    setUsers((prevUsers) =>
      prevUsers.map((u) =>
        u.id === userId
          ? { ...u, status: newStatus, callbackDate: "", callbackTime: "", scheduleDate: "", scheduleTime: "" }
          : u,
      ),
    )
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({
        ...selectedUser,
        status: newStatus,
        callbackDate: "",
        callbackTime: "",
        scheduleDate: "",
        scheduleTime: "",
      })
    }
    if (newStatus === "Call Back") {
      setCallbackModalUserId(userId)
      // For Call Back, continue to update API immediately
    }
    if (newStatus === "Schedule") {
      // Don't call updateApproach for Schedule, let the modal handle navigation
      return
    }
    try {
      const updated = await updateApproach(userId, { callStatus: newStatus })
      if (updated?.webhookEvents || updated?.whatsappChat) {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === userId
              ? {
                  ...u,
                  webhookEvents: updated.webhookEvents ?? u.webhookEvents,
                  whatsappChat: updated.whatsappChat ?? u.whatsappChat,
                }
              : u,
          ),
        )
      }
    } catch (error) {
      // Revert UI change on error
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId ? { ...u, status: previousStatus } : u,
        ),
      )
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, status: previousStatus })
      }
      console.error("Failed to update status:", error)
    }
  }

  const handleCallbackSubmit = async (userId: string, date: string, time: string) => {
    setUsers((prevUsers) =>
      prevUsers.map((u) => (u.id === userId ? { ...u, callbackDate: date, callbackTime: time } : u)),
    )
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({ ...selectedUser, callbackDate: date, callbackTime: time })
    }
    try {
      await updateApproach(userId, {
        callBackTime: `${date}T${time}`,
      })
    } catch (error) {
      // Optionally show error or revert UI change
      console.error("Failed to update callback:", error)
    }
  }

  const handleNoteModalSubmit = async (note: string) => {
    if (!noteModal) return
    const { userId, mode } = noteModal
    const previousUser = users.find((u) => u.id === userId)
    setNoteModalSubmitting(true)
    try {
      if (mode === "not_interested") {
        await updateApproach(userId, { callStatus: "Not Interested", remarks: note })
        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, status: "Not Interested", remarks: note } : u)),
        )
        if (selectedUser?.id === userId) {
          setSelectedUser({ ...selectedUser, status: "Not Interested", remarks: note })
        }
        toast.success("Marked as Not Interested")
      } else {
        await updateApproach(userId, { remarks: note })
        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, remarks: note } : u)),
        )
        if (selectedUser?.id === userId) {
          setSelectedUser({ ...selectedUser, remarks: note })
        }
        toast.success("Note saved")
      }
      setNoteModal(null)
    } catch (error) {
      console.error("Failed to save approach note:", error)
      toast.error(
        mode === "not_interested"
          ? "Failed to mark Not Interested"
          : "Failed to save note",
      )
      if (mode === "not_interested" && previousUser) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId
              ? { ...u, status: previousUser.status, remarks: previousUser.remarks }
              : u,
          ),
        )
      }
    } finally {
      setNoteModalSubmitting(false)
    }
  }

  const handleScheduleSubmit = (userId: string, date: string, time: string) => {
    setUsers((prevUsers) =>
      prevUsers.map((u) => (u.id === userId ? { ...u, scheduleDate: date, scheduleTime: time } : u)),
    )
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({ ...selectedUser, scheduleDate: date, scheduleTime: time })
    }
  }

  const handleAddTPO = () => {
    setShowAddModal(true)
  }

  const handleAddUser = (user: { name: string; number: string; email: string; qualifications: string }) => {
    // Check for duplicates
    const duplicate = users.find(
      (u) =>
        u.phone.trim() === user.number.trim() ||
        u.name.trim().toLowerCase() === user.name.trim().toLowerCase()
    )
    if (duplicate) {
      // toast.error("User with same email, phone, or name already exists!")
      return false
    }
    setUsers((prev) => [
      ...prev,
      {
        id: (prev.length + 1).toString(),
        name: user.name,
        phone: user.number,
        email: user.email,
        approachedBy: "",
        ledBy: "",
        qualifications: user.qualifications,
        source: "",
        status: "Ringing",
      },
    ])
    setShowAddModal(false)
    return true
  }

  const handleUserEdit = (user: User) => {
    setSelectedUserForEdit(user)
    setShowEditApproachModal(true)
  }

  const handleReassignUser = (user: User) => {
    setUserToReassign(user)
    setIsReassignModalOpen(true)
  }

  const handleReassignSuccess = () => {
    setIsReassignModalOpen(false)
    setUserToReassign(null)
  }

  const handleUserUpdate = (updatedUser: User) => {
    setUsers((prevUsers) => prevUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)))
    setSelectedUser(updatedUser)
  }


  const fetchRecruiters = useCallback(async () => {
    setIsLoadingRecruiters(true)
    try {
      const response = await getMySubordinates()
      if (response?.data?.subordinates) {
        setRecruiters((prev) => [...prev, ...response.data.subordinates])
      } else {
        setRecruiters([])
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err)
      setRecruiters([])
    } finally {
      setIsLoadingRecruiters(false)
    }
  }, [])

  useEffect(() => {
    fetchRecruiters()
  }, [fetchRecruiters])

  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true)
    try {
      const response = await getJobsForSelection({ _t: Date.now() })
      const jobs = response?.data?.jobs || response?.data || []
      const mappedOptions: JobFilterOption[] = Array.isArray(jobs)
        ? jobs.map((job: any) => {
            const jobId = job?._id || job?.id || ""
            const title = job?.title || "Untitled Job"
            const companyName = job?.companyId?.name || job?.companyName || ""
            return {
              value: jobId,
              label: companyName ? `${title} - ${companyName}` : title,
            }
          }).filter((option: JobFilterOption) => Boolean(option.value))
        : []
      setJobOptions(mappedOptions)
    } catch (error) {
      setJobOptions([])
    } finally {
      setIsLoadingJobs(false)
    }
  }, [])

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    setCursor(null)
    setNextCursor(null)
    setHasNextPage(false)
  }, [showAddModal, showEditApproachModal])

  // Export to Excel function
  const handleExportExcel = async () => {
    setIsExportingExcel(true)
    try {
      toast.loading("Preparing export...", { id: "export-excel" })

      const hasActiveFilters = Boolean(
        searchTerm.trim() ||
          filters.source ||
          filters.status ||
          filters.appStatus ||
          filters.recruiterId ||
          filters.jobId ||
          filters.fromDate ||
          filters.toDate ||
          filters.whatsappOutcome
      )

      const EXPORT_BATCH_SIZE = 5000
      const MAX_EXPORT_ROWS = 200000
      const baseParams = {
        search: searchTerm || "",
        source: filters.source || "",
        callStatus: filters.status || "",
        recruiterId: filters.recruiterId || "",
        jobId: filters.jobId || "",
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        whatsappOutcome: filters.whatsappOutcome || "",
        ...(isCommunicationView ? { hasWebhookEvents: "true" } : {}),
      }

      let allApproaches: any[] = []
      let exportCursor: string | null = null
      let exportHasNextPage = true
      let totalResults = 0

      // No filters → only first page (same page size as the table)
      if (!hasActiveFilters) {
        const response: ExportApproachesResponse = await exportApproaches({
          ...baseParams,
          limit: itemsPerPage,
        })
        allApproaches = response?.approaches || []
        totalResults = response?.totalResults ?? allApproaches.length
        exportHasNextPage = false
      } else {
        while (exportHasNextPage && allApproaches.length < MAX_EXPORT_ROWS) {
          const remaining = MAX_EXPORT_ROWS - allApproaches.length
          const response: ExportApproachesResponse = await exportApproaches({
            ...baseParams,
            ...(exportCursor ? { cursor: exportCursor } : {}),
            limit: Math.min(EXPORT_BATCH_SIZE, remaining),
          })

          const batch = response?.approaches || []
          totalResults = response?.totalResults ?? totalResults
          allApproaches = allApproaches.concat(batch)
          exportHasNextPage = Boolean(response?.hasNextPage) && allApproaches.length < MAX_EXPORT_ROWS
          exportCursor = response?.nextCursor || null

          const progressLabel = totalResults > 0
            ? `Exporting ${allApproaches.length.toLocaleString()} of ${totalResults.toLocaleString()} rows...`
            : `Exporting ${allApproaches.length.toLocaleString()} rows...`
          toast.loading(progressLabel, { id: "export-excel" })

          if (batch.length === 0) {
            break
          }
        }
      }

      if (allApproaches.length === 0) {
        toast.error("No data to export", { id: "export-excel" })
        return
      }

      // Map data to Excel format
      const excelData = allApproaches.map((u: any) => {
        let callbackDate = "N/A"
        let callbackTime = "N/A"
        if (u.callBackTime) {
          const dt = new Date(u.callBackTime)
          callbackDate = dt.toLocaleDateString('en-CA').slice(0, 10)
          callbackTime = dt.toTimeString().slice(0, 5)
        }
        return {
          "Name": u.name || "",
          "Phone": u.phone || "",
          "Email": u.email || "",
          "Approached By": u.recruiter?.email || u.recruiter?.username || "",
          "Led By": u.manager?.email || u.manager?.username || "",
          "Qualifications": u.qualifications || "",
          "City": u.city || "",
          "Source": u.source || "",
          "Status": u.callStatus || "",
          "WhatsApp Outcome": u.whatsappChat?.outcome || "",
          "Callback Date": callbackDate,
          "Callback Time": callbackTime,
        }
      })

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(excelData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Approaches")

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
      const blob = new Blob([excelBuffer], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `approaches_${format(new Date(), 'yyyy-MM-dd')}.xlsx`)
      document.body.appendChild(link)
      link.click()

      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)

      if (!hasActiveFilters) {
        toast.success(
          `Downloaded first page (${allApproaches.length} row${allApproaches.length === 1 ? "" : "s"}). Apply filters to export more.`,
          { id: "export-excel", duration: 5000 }
        )
      } else {
        const truncated = exportHasNextPage || (totalResults > 0 && allApproaches.length < totalResults)
        if (truncated) {
          toast.success(
            `Exported ${allApproaches.length.toLocaleString()} of ${totalResults.toLocaleString()} rows. Narrow filters to export a smaller set.`,
            { id: "export-excel", duration: 6000 }
          )
        } else {
          toast.success("Excel file downloaded successfully!", { id: "export-excel" })
        }
      }
    } catch (error: any) {
      console.error("Failed to export Excel:", error)
      toast.error(`Failed to export: ${error?.response?.data?.message || error?.message || 'Unknown error'}`, { 
        id: "export-excel" 
      })
    } finally {
      setIsExportingExcel(false)
    }
  }

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const params: Record<string, string | number | undefined> = {
          search: searchTerm || "",
          source: filters.source || "",
          callStatus: filters.status || "",
          appStatus: filters.appStatus || "",
          recruiterId: filters.recruiterId || "",
          jobId: filters.jobId || "",
          hasWebhookEvents: location.pathname === "/ApprochesView2" ? "true" : undefined,
          fromDate: filters.fromDate || "",
          toDate: filters.toDate || "",
          whatsappOutcome: filters.whatsappOutcome || "",
          cursor: cursor || undefined,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
        }
        const cleanedParams = Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== "",
          ),
        )
        const data = await getApproaches(cleanedParams)
        console.log("Fetched approaches data:", data)

        // Map backend fields to frontend User shape
        const mappedUsers = Array.isArray(data.data) ? data.data.map((u: any) => {
              let callbackDate = ""
              let callbackTime = ""
              if (u.callBackTime) {
                const dt = new Date(u.callBackTime)
                callbackDate = dt.toLocaleDateString('en-CA').slice(0, 10) // yyyy-mm-dd
                callbackTime = dt.toTimeString().slice(0, 5) // HH:mm
              }
              return {
                id: u._id,
                name: u.name,
                phone: u.phone,
                email: u.email,
                approachedBy: u.recruiter?.email || u.recruiter?.username || "",
                ledBy: u.manager?.email || u.manager?.username || "",
                qualifications: u.qualifications,
                city: u.city || "",
                source: u.source,
                status: u.callStatus,
                createdAt: u.createdAt || "",
                callbackDate,
                callbackTime,
                scheduleDate: u.scheduleDate || "",
                scheduleTime: u.scheduleTime || "",
                jobId: u.jobId,
                applicationId: u.applicationId,
                application: u.application,
                applications: u.application,
                recruiter: u.recruiter,
                manager: u.manager,
                remarks: u.remarks || "",
                webhookEvents: u.webhookEvents,
                whatsappChat: u.whatsappChat,
              }
            })
          : []
        setUsers(mappedUsers)
        setHasNextPage(Boolean(data.hasNextPage))
        setNextCursor(data.nextCursor || null)
        const parsedTotalResults = Number(data.totalResults)
        if (Number.isFinite(parsedTotalResults) && parsedTotalResults >= 0) {
          setTotalApproaches(parsedTotalResults)
        }
      } catch (error) {
        setUsers([])
        setHasNextPage(false)
        setNextCursor(null)
        setTotalApproaches(0)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [searchTerm, filters, cursor, itemsPerPage, sortBy, sortOrder, showAddModal, showEditApproachModal, location.pathname])

  return (
    <div data-page-scroll="internal" className="bg-background flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-background border-b border-border z-40">
        <div className="px-3 sm:px-6 py-4">
          <div className="space-y-4">
            {/* Search Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by name, number, email, led by, qualifications"
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchTerm(e.target.value)
                    setCursor(null)
                    setNextCursor(null)
                    setHasNextPage(false)
                  }}
                  className="pl-10 w-full"
                />
              </div>
              <Select
                value={filters.status || "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({ ...prev, status: value === "all" ? "" : value }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
              >
                <SelectTrigger className="w-full sm:w-48 flex-shrink-0">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {(isCommunicationView ? communicationStatusFilterOptions : statusOptions).map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isCommunicationView && (
                <>
              <Button variant="default" size="sm" onClick={handleAddTPO} className="bg-[#EC6E51] hover:bg-[#d65f47] flex-shrink-0">
                Add
              </Button>
              {canDownloadExcel && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={isExportingExcel}
                className="hover:bg-gray-200 flex-shrink-0 flex items-center gap-2"
              >
                {isExportingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export
                  </>
                )}
              </Button>
              )}
                </>
              )}
              {isCommunicationView && canDownloadExcel && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={isExportingExcel}
                  className="hover:bg-gray-200 flex-shrink-0 flex items-center gap-2"
                >
                  {isExportingExcel ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Excel
                    </>
                  )}
                </Button>
              )}
            </div>
            
            {/* Filters Grid — AI Response has 5 filters (no Source / App Status); Approaches has 6 */}
            <div
              className={
                isCommunicationView
                  ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3"
                  : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3"
              }
            >
              {!isCommunicationView && (
              <SearchableSelect
                options={sourceOptions}
                value={filters.source || "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({ ...prev, source: value === "all" ? "" : value }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
                placeholder="All Sources"
                emptyText="No sources found"
                searchPlaceholder="Search source..."
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                searchable
              />
              )}
              {isCommunicationView && (
              <Select
                value={filters.whatsappOutcome || "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    whatsappOutcome: value === "all" ? "" : value,
                  }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="WhatsApp Outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All WhatsApp Outcomes</SelectItem>
                  {WHATSAPP_OUTCOME_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
              <SearchableSelect
                options={recruiterOptions}
                value={filters.recruiterId || "all"}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, recruiterId: v === "all" ? "" : v }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
                placeholder={isLoadingRecruiters ? "Loading..." : "All Recruiters"}
                emptyText="No recruiters found"
                searchPlaceholder="Search by username..."
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                loading={isLoadingRecruiters}
                icon={<UserIcon className="h-4 w-4 text-gray-500" />}
                searchable
              />
              <div className="w-full">
                <ReactSelect
                  options={jobOptions}
                  value={jobOptions.find((option) => option.value === filters.jobId) || null}
                  onChange={(selectedOption) => {
                    setFilters((prev) => ({
                      ...prev,
                      jobId: selectedOption?.value || "",
                    }))
                    setCursor(null)
                    setNextCursor(null)
                    setHasNextPage(false)
                  }}
                  isClearable
                  isLoading={isLoadingJobs}
                  placeholder={isLoadingJobs ? "Loading jobs..." : "All Jobs"}
                  classNamePrefix="job-filter-select"
                  className="w-full"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "40px",
                      borderColor: "#e5e7eb",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingLeft: "10px",
                      paddingRight: "10px",
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      display: "none",
                    }),
                  }}
                  noOptionsMessage={() => "No jobs found"}
                />
              </div>
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
                className="w-full"
                placeholder="From Date"
              />
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, toDate: e.target.value }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
                className="w-full"
                placeholder="To Date"
              />
              {/* App Status filter hidden on EJ AI Response (/ApprochesView2) */}
              {!isCommunicationView && (
              <Select
                value={filters.appStatus || "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({ ...prev, appStatus: value === "all" ? "" : value }))
                  setCursor(null)
                  setNextCursor(null)
                  setHasNextPage(false)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="App Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All App Status</SelectItem>
                  {appStatusOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </div>
            {(activeFilters.length > 0 || searchTerm) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: {searchTerm}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => {
                        setSearchTerm("")
                        setCursor(null)
                        setNextCursor(null)
                        setHasNextPage(false)
                      }}
                    />
                  </Badge>
                )}
                {Object.entries(filters)
                  .filter(([_, value]) => value !== "")
                  .map(([key, value]) => (
                    <Badge key={key} variant="secondary" className="flex items-center gap-1">
                      {getFilterLabel(key)}:{" "}
                      {key === "recruiterId"
                        ? recruiters.find((r) => r.id === value)?.username || value
                        : key === "jobId"
                          ? jobOptions.find((job) => job.value === value)?.label || value
                          : key === "whatsappOutcome"
                            ? WHATSAPP_OUTCOME_FILTER_OPTIONS.find((o) => o.value === value)?.label || value
                          : value}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeFilter(key)} />
                    </Badge>
                  ))}
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs">
                  Clear all
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <div className={`transition-all duration-300 ${selectedUser ? "mr-96" : ""}`}>
          <UserTable
              users={users}
              jobOptions={jobOptions}
              setUsers={setUsers}
              onUserSelect={setSelectedUser}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
              onUserReassign={handleReassignUser}
              onSourceChange={handleSourceChange}
              onStatusChange={handleStatusChange}
              onCallbackSubmit={handleCallbackSubmit}
              onScheduleSubmit={handleScheduleSubmit}
              onRequestNotInterested={(userId) =>
                setNoteModal({ userId, mode: "not_interested" })
              }
              onRequestNote={(userId) => setNoteModal({ userId, mode: "note" })}
              setCallbackModalUserId={setCallbackModalUserId}
              isLoading={isLoading}
              onUserEdit={handleUserEdit}
              isCommunicationView={isCommunicationView}
            />
          </div>
      </div>
        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 flex-shrink-0 bg-white border-t border-gray-200">
          <div className="text-sm text-gray-500 text-center sm:text-left">
            Showing {users.length} candidates · Total approaches: {totalApproaches}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (nextCursor) setCursor(nextCursor)
              }}
              disabled={!hasNextPage || isLoading || !nextCursor}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

     {!["MASTER_BDE","FREELANCE_BDE","BDE_INTERN"].includes(user?.role as string) ?  <AddUserModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onAdd={handleAddUser} /> : <AddCompanyModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onAdd={handleAddUser} />}
      <CallbackModal
        isOpen={!!callbackModalUserId}
        onClose={() => setCallbackModalUserId(null)}
        onSubmit={(date, time) => {
          if (callbackModalUserId) {
            handleCallbackSubmit(callbackModalUserId, date, time)
            setCallbackModalUserId(null)
          }
        }}
        initialDate={users.find((u) => u.id === callbackModalUserId)?.callbackDate}
        initialTime={users.find((u) => u.id === callbackModalUserId)?.callbackTime}
      />
      <ApproachNoteModal
        isOpen={!!noteModal}
        mode={noteModal?.mode || "note"}
        candidateName={users.find((u) => u.id === noteModal?.userId)?.name}
        initialNote={
          noteModal?.mode === "note"
            ? users.find((u) => u.id === noteModal.userId)?.remarks || ""
            : ""
        }
        isSubmitting={noteModalSubmitting}
        onClose={() => {
          if (!noteModalSubmitting) setNoteModal(null)
        }}
        onSubmit={handleNoteModalSubmit}
      />
      <ScheduleConfirmModal
        isOpen={!!scheduleConfirmUserId}
        onClose={() => {
          // Revert status to previous when cancelled
          if (scheduleConfirmUserId && previousStatusForSchedule) {
            setUsers((prevUsers) =>
              prevUsers.map((u) =>
                u.id === scheduleConfirmUserId
                  ? { ...u, status: previousStatusForSchedule }
                  : u,
              ),
            )
            if (selectedUser && selectedUser.id === scheduleConfirmUserId) {
              setSelectedUser({
                ...selectedUser,
                status: previousStatusForSchedule,
              })
            }
          }
          setScheduleConfirmUserId(null)
          setPreviousStatusForSchedule(null)
        }}
        onConfirm={() => {
          const scheduledUser = users.find(u => u.id === scheduleConfirmUserId)
          console.log('Navigating with scheduledUser:', scheduledUser, 'jobId:', scheduledUser?.jobId)
          setShowScheduleUIFor(scheduleConfirmUserId)
          setScheduleConfirmUserId(null)
          setPreviousStatusForSchedule(null)
          navigate("/add-candidate", {
            state: {
              fromApproaches: true,
              existingApproachId: scheduledUser?.id,
              approachPayload: {
                name: scheduledUser?.name || '',
                phone: scheduledUser?.phone || '',
                email: scheduledUser?.email || '',
                qualifications: scheduledUser?.qualifications || '',
                source: scheduledUser?.source || '',
                callStatus: "Schedule",
                remarks: scheduledUser?.remarks || '',
                jobId: scheduledUser?.jobId || '',
              },
              prefill: {
                dateOfBirth: scheduledUser?.dob || scheduledUser?.dateOfBirth || '',
                skills: Array.isArray(scheduledUser?.skills) ? scheduledUser?.skills : [],
                workExperience: scheduledUser?.workExperience || 0,
              },
            },
          })
        }}
      />
      <AddApproachModal
        isOpen={showEditApproachModal}
        onClose={() => {
          setShowEditApproachModal(false)
          setSelectedUserForEdit(null)
        }}
        candidate={selectedUserForEdit ? {
          id: selectedUserForEdit.id,
          name: selectedUserForEdit.name,
          email: selectedUserForEdit.email,
          phone: selectedUserForEdit.phone,
          highestQualification: selectedUserForEdit.qualifications,
          profileSource: selectedUserForEdit.source,

        } : null}
      />
    </div>
  )
}

export default ApproachesView